const CE_PREFIX = /^\\ce\{/
const CE_SUFFIX = /\}$/
const PU_PREFIX = /^\\pu\{/

export type ChemistryLatexParts = {
  isChemistry: boolean
  body: string
  command: "ce" | "pu" | null
}

/** Detects \\ce{...} or \\pu{...} wrappers used for chemistry in stored LaTeX. */
export const parseChemistryLatex = (latex: string): ChemistryLatexParts => {
  const trimmed = latex.trim()
  if (!trimmed) {
    return { isChemistry: false, body: "", command: null }
  }

  if (PU_PREFIX.test(trimmed) && trimmed.endsWith("}")) {
    return {
      isChemistry: true,
      body: trimmed.slice(4, -1),
      command: "pu",
    }
  }

  if (CE_PREFIX.test(trimmed) && trimmed.endsWith("}")) {
    return {
      isChemistry: true,
      body: trimmed.slice(4, -1),
      command: "ce",
    }
  }

  return { isChemistry: false, body: trimmed, command: null }
}

/** Raw mhchem syntax for MathLive — no \\ce/\\pu wrapper (text mode editing). */
export const toChemistryEditorValue = (latex: string): string => {
  const trimmed = latex.trim()
  if (!trimmed) return ""

  const parsed = parseChemistryLatex(trimmed)
  if (parsed.isChemistry) return parsed.body

  return trimmed
}

/** LaTeX persisted in the editor — always wrapped for chemistry inserts. */
export const toStoredChemistryLatex = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ""

  const parsed = parseChemistryLatex(trimmed)
  if (parsed.command === "pu") return `\\pu{${parsed.body}}`
  if (parsed.isChemistry) return `\\ce{${parsed.body}}`

  return `\\ce{${trimmed}}`
}

/** KaTeX input for live preview in the chemistry modal. */
export const toChemistryPreviewLatex = (value: string): string => {
  const trimmed = value.trim()
  if (!trimmed) return ""

  const parsed = parseChemistryLatex(trimmed)
  if (parsed.command === "pu") return `\\pu{${parsed.body}}`
  if (parsed.isChemistry) return `\\ce{${parsed.body}}`

  return `\\ce{${trimmed}}`
}
